<script setup lang="ts">
import { onMounted, nextTick } from 'vue'
import type { ImpactColor, ImpactType, TotalImpact } from '@/api/interfaces'
import type { IconStyleType } from '@/api/interfaces'
import type { PublicStatData } from '@/stores/interfaces/PublicData'
import { computed } from 'vue'
import { getCSSVariable, getImageUrl } from '@/helpers/utils'
import { useEditorStore } from '@/stores/Editor'
import { usePublicData } from '@/stores/PublicData'

import PublicTopStatCard from '@/components/public-page/PublicTopStatCard.vue'
import BackgroundIcon from '@/components/icons/backgrounds/BackgroundIcon.vue'

interface Props {
  publicStatData: PublicStatData
  topStatData: TotalImpact
  colorSettings: Record<ImpactType, ImpactColor>
  iconStyle: IconStyleType
}

const props = defineProps<Props>()
const emit = defineEmits<{
  ready: []
}>()

const defaultColorSettings = {
  backgroundColor: getCSSVariable('gray-light-db'),
  textColor: getCSSVariable('ui-black'),
}

const publicDataStore = usePublicData()
const store = useEditorStore()

const getRelationImpact = (type: ImpactType) => {
  return props.publicStatData?.relationImpacts?.[type] || 0
}

const getTransferredImpact = (type: ImpactType) => {
  return props.publicStatData?.transferredImpacts[type] || 0
}

const topStatDataToDisplay = computed<TotalImpact>(() => {
  const validEntries = Object.entries(props.topStatData).filter(([key, value]) =>
    showCard(key as ImpactType, value),
  ) as [ImpactType, number][]

  return validEntries.reduce<TotalImpact>((acc, [key, value]) => {
    acc[key] = value
    return acc
  }, {} as TotalImpact)
})

const hasImpacts = computed<boolean>(() => Object.keys(topStatDataToDisplay.value).length > 0)

const showNewMemberLabel = computed<boolean>(
  () =>
    Object.values(props.topStatData).every((v) => v === 0) &&
    store.editor.icon.impactDisplay.hideZeroValues &&
    publicDataStore.impacts !== undefined,
)

const showCard = (type: ImpactType, value: number): boolean => {
  const displayImpact = store.editor.icon.impactDisplay[type]
  const { hideZeroValues } = store.editor.icon.impactDisplay

  return displayImpact && ((value === 0 && !hideZeroValues) || value > 0)
}

onMounted(async () => {
  await nextTick()
  window.requestAnimationFrame(() => emit('ready'))
})
</script>

<template>
  <section
    :class="[
      'section',
      'section--lg',
      store.editor.background.theme,
      { 'has-impacts': hasImpacts },
    ]"
  >
    <BackgroundIcon
      class="background-icon background-icon--left"
      align="left"
      :icon-type="store.editor.background.icon"
    />
    <v-container v-if="hasImpacts">
      <div v-if="$route.name === 'PublicPage'" class="section-header">
        <h2 class="section-title">{{ $t(`PublicTopStatCardList.title`) }}</h2>
      </div>
      <div class="top-stat-card-list">
        <template v-for="(value, key) in topStatDataToDisplay" :key="key">
          <PublicTopStatCard
            :type="key"
            :amount="value"
            :transferred-impact="getTransferredImpact(key)"
            :relation-impact="getRelationImpact(key)"
            :color-settings="colorSettings[key] || defaultColorSettings"
            :icon-style="iconStyle"
          />
        </template>
      </div>
    </v-container>
    <v-container class="placeholder-container" v-else-if="showNewMemberLabel">
      <div class="new-member-banner">
        <div class="new-member-banner-text">
          <h4>{{ $t(`PublicTopStatCardList.new_member.title`) }}</h4>
          <p v-html="$t(`PublicTopStatCardList.new_member.description`)" />
        </div>
        <img
          class="new-member-banner-image"
          :src="getImageUrl('/src/assets/images/new-member-impacts.svg')"
          alt="new-member-impacts"
        />
      </div>
      <h3 class="check-below">{{ $t(`PublicTopStatCardList.check_below`) }}</h3>
    </v-container>
    <BackgroundIcon
      class="background-icon--right"
      align="right"
      variant="outline"
      :icon-type="store.editor.background.icon"
    />
  </section>
</template>

<style scoped lang="scss">
.section {
  position: relative;

  .community-page & {
    margin-top: 10px;

    @include media('md-and-up') {
      margin: 0;
      top: -100px;
    }
  }

  &:not(.has-impacts) {
    margin-bottom: 16px;

    @include media('lg-and-up') {
      margin-bottom: 48px;
    }
  }

  :deep(.background-icon) {
    display: none;

    &.background-icon--left {
      .community-page & {
        display: none;
      }
    }

    @include media('md-and-up') {
      display: block;
      position: absolute;
      top: 200%;
      left: -40px;
      z-index: 0;
      width: 175px;
      height: 140px;
    }
  }

  :deep(.background-icon--right) {
    display: none;

    .community-page & {
      @include media('md-and-down') {
        display: block;
        position: absolute;
        top: -80px;
        right: -35px;
        width: 110px;
        height: 90px;
      }
    }

    @include media('md-and-up') {
      display: block;
      position: absolute;
      top: 30px;
      right: -60px;
      z-index: 0;
      width: 240px;
      height: 200px;
      transform: rotate(-10deg);

      .community-page & {
        top: -10px;
        right: -10px;
        transform: rotate(0deg) scale(1.75);
      }
    }
  }
}

.top-stat-card-list {
  position: relative;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  width: 100%;

  @include media('lg-and-up') {
    gap: 24px;
  }
}

.placeholder-container {
  text-align: center;
}

.new-member-banner {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;
  background-color: var(--ui-dark-green);
  color: var(--ui-white);
  padding: 14px;
  border-radius: 10px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 40px;

  @include media('md-and-up') {
    gap: 42px;
    padding: 26px 21px;
    margin-bottom: 64px;
  }

  .new-member-banner-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 4px;
    text-align: left;

    h4 {
      font-size: 24px;
      font-weight: 700;
      line-height: 29px;
      margin-bottom: 0;

      @include media('md-and-up') {
        font-size: 40px;
        line-height: 49px;
      }
    }

    p {
      font-size: 12px;
      font-weight: 400;
      line-height: 15px;
      margin-bottom: 0;

      @include media('md-and-up') {
        font-size: 20px;
        line-height: 24px;
      }
    }
  }

  .new-member-banner-image {
    height: auto;
    max-width: 43px;
    margin: 16px 18px;

    @include media('md-and-up') {
      max-width: unset;
      margin: 0 28px;
    }
  }
}

.check-below {
  font-size: 20px;
  font-weight: 400;
  line-height: 24px;
  text-align: center;
  padding: 0 18px;

  @include media('md-and-up') {
    font-size: 32px;
    line-height: 39px;
    padding: 0;
  }
}
</style>
